import type { Ref } from 'vue';
import {
  computed,
} from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';

import type {
  IAffiliateProgramSubAffiliateDealsEditableFields,
  IAffiliateProgramSubAffiliateDealsSavePayload,
} from '@modules/affiliate-program-subaffiliate-deals/submodules/edit/types';
import type { IApiFormErrors } from '@modules/core/services/query/types';
import type {
  FormGroup,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import {
  AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS_LIST_ROUTE_NAME,
  TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS,
} from '@modules/affiliate-program-subaffiliate-deals/constants';
import { useAffiliateProgramSubAffiliateDealEditMutation } from '@modules/affiliate-program-subaffiliate-deals/submodules/edit/mutations';
import {
  useAffiliateProgramSubAffiliateDealsConstantsListQuery,
  useAffiliateProgramSubAffiliateDealsItemQuery,
} from '@modules/affiliate-program-subaffiliate-deals/submodules/edit/queries';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';

export interface AffiliateProgramSubAffiliateDealsEditComposable {
  isEdit: Ref<boolean>;
  isLoading: Ref<boolean>;
  constants: Ref<IFormSelectInputOption<string>[] | undefined>;
  formSchema: Ref<FormSchema<IAffiliateProgramSubAffiliateDealsEditableFields, 'subaffiliate_condition_steps_attributes'>>;
  groups: Ref<FormGroup<IAffiliateProgramSubAffiliateDealsEditableFields, 'subaffiliate_condition_steps_attributes'>[]>;
  customErrors: Ref<IApiFormErrors | undefined>;
  isLoadingSave: Ref<boolean>;
  item: Ref<IAffiliateProgramSubAffiliateDealsSavePayload | undefined>;
  clearError(key: string): void;
  onSubmit(payload: IAffiliateProgramSubAffiliateDealsEditableFields): Promise<void>;
}

export default function useAffiliateProgramSubAffiliateDealsEdit(): AffiliateProgramSubAffiliateDealsEditComposable {
  const { getTranslation } = useI18n();
  const router = useRouter();
  const route = useRoute();

  const isEdit = computed(() => !!route.params.id);
  const id = computed<number | undefined>(() => (
    route.params.id ? Number(route.params.id) : undefined
  ));

  const { data: constants, isLoading: isConstantsLoading } = useAffiliateProgramSubAffiliateDealsConstantsListQuery();
  const { data: item, isLoading: isLoadingItem } = useAffiliateProgramSubAffiliateDealsItemQuery(id);

  const isLoading = computed(() => (
    isConstantsLoading.value || (!!id.value && isLoadingItem.value)
  ));

  const formSchema = computed<FormSchema<IAffiliateProgramSubAffiliateDealsEditableFields, 'subaffiliate_condition_steps_attributes'>>(() => ({
    title: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'FORM_TITLE').value,
      required: true,
      value: item.value?.title,
    },
    description: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'FORM_DESCRIPTION').value,
      required: true,
      value: item.value?.description,
    },
    subaffiliate_condition_steps_attributes: {
      type: EFormInputTypes.SLOT,
      required: true,
      requiredErrorText: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'STEP_INVALID').value,
      value: item.value?.subaffiliate_condition_steps_attributes,
    },
  }));

  const groups = computed<FormGroup<IAffiliateProgramSubAffiliateDealsEditableFields, 'subaffiliate_condition_steps_attributes'>[]>(() => ([{
    items: ['title', 'description'],
  }, {
    title: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'STEP_SETTINGS_HEADER').value,
    items: ['subaffiliate_condition_steps_attributes'],
  }]));

  const {
    mutateAsync,
    errors: customErrors,
    clearError,
    isLoading: isLoadingSave,
  } = useAffiliateProgramSubAffiliateDealEditMutation();

  async function onSubmit(payload: IAffiliateProgramSubAffiliateDealsEditableFields): Promise<void> {
    await mutateAsync({
      ...(item.value ?? {}),
      ...payload,
    });
    router.back({
      name: AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS_LIST_ROUTE_NAME,
    });
  }

  return {
    isEdit,
    isLoading,
    formSchema,
    groups,
    constants,
    customErrors,
    isLoadingSave,
    item,
    clearError,
    onSubmit,
  };
}
